import React, { useState, useEffect } from 'react';
import { oracleDeck, uiText } from '../../data/oracle/oracleDeckData';
import { useGlow } from '../../hooks/useGlow';
import { LockClosedIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { getOracleReading } from '../../services/geminiService'; 
import AIReadingDisplay from './AIReadingDisplay'; 
import OracleCreditManager from './OracleCreditManager';
import CooldownTimer from '../common/CooldownTimer';

const UNLOCK_CODE = "PORTALNUEVO"; 
const STORAGE_KEY = "tripleEnergyOracle";

// --- SUBCOMPONENTE: Carta Individual ---
const CardSlot = ({ card, isReversed, isRevealed, onClick, positionLabel, isCenter }) => {
  return (
    <div className={`flex flex-col items-center gap-4 ${isCenter ? 'md:-mt-10' : ''}`}>
      <span className={`text-sm font-bold tracking-widest uppercase opacity-80 h-6 ${isCenter ? 'text-amber-400' : 'text-purple-300'}`}>
        {positionLabel}
      </span>

      <div 
        onClick={!isRevealed ? onClick : undefined}
        className={`relative w-44 h-64 sm:w-48 sm:h-72 cursor-pointer perspective-1000 group ${!isRevealed ? 'hover:-translate-y-2' : ''} transition-transform duration-300`}
      >
        <div className={`relative w-full h-full duration-700 preserve-3d transition-transform ${isRevealed ? 'rotate-y-180' : ''}`}>
          <div className="absolute inset-0 backface-hidden w-full h-full">
            <img src={oracleDeck[0].cardBack} alt="Reverso" className="w-full h-full object-cover rounded-xl border-2 border-purple-400/30 shadow-lg" />
          </div>
          <div className="absolute inset-0 backface-hidden w-full h-full rotate-y-180 bg-gray-900 rounded-xl overflow-hidden border-2 border-sky-400/50 shadow-2xl">
            <img src={card.image} alt={card.data.name} className={`w-full h-full object-cover transition-transform duration-700 ${isReversed ? 'rotate-180' : ''}`} />
            {isReversed && <div className="absolute inset-0 bg-indigo-900/10 pointer-events-none mix-blend-overlay"></div>}
          </div>
        </div>
      </div>

      <div className={`text-center transition-all duration-700 flex flex-col items-center gap-1 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
        <span className="text-sky-400 text-[10px] font-bold tracking-widest uppercase bg-sky-950/30 px-2 py-0.5 rounded border border-sky-500/20">
            {card.data.category}
        </span>
        <h3 className="text-gray-100 font-bold text-xl leading-tight uppercase tracking-tight">
            {card.data.name}
        </h3>
        {isReversed && (
            <span className="text-red-400 text-[10px] uppercase font-black border border-red-400/30 px-2 py-0.5 rounded-full mt-1 bg-red-900/20">
                Invertida
            </span>
        )}
      </div>
    </div>
  );
};

// --- COMPONENTE PRINCIPAL ---
const TripleEnergySpread = ({ lang = 'es' }) => {
  const [cards, setCards] = useState([]); 
  const [topic, setTopic] = useState('');
  const [revealedCount, setRevealedCount] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [aiReading, setAiReading] = useState('');
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [storedDate, setStoredDate] = useState(null);

  const localUi = {
    spreadTitle: lang === 'en' ? 'Triple Energy Reading' : 'Tirada de Triple Energía',
    spreadIntro: lang === 'en' ? 'Center your intention: What energy do you want to explore?' : 'Centra tu intención: ¿Qué energía deseas explorar?',
    inputPlaceholder: lang === 'en' ? 'Ex: My current path, A project...' : 'Ej: Mi camino actual, Un proyecto...',
    pos1: lang === 'en' ? 'Supportive Energy' : 'Energía de Apoyo',
    pos2: lang === 'en' ? 'Core Energy' : 'Energía Central',
    pos3: lang === 'en' ? 'Complementary Energy' : 'Energía Complementaria',
    aiBtn: lang === 'en' ? 'Interpret the Energy' : 'Interpretar la Energía',
    dailyLimitMsg: lang === 'en' ? 'You have already done your reading for today.' : 'Ya has realizado tu lectura de hoy.'
  };

  const titleStyle = useGlow({ gradient: 'linear-gradient(to bottom, #fff, #fcd34d)' });

  // EFECTO: Recuperar estado de sesión
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { date, cards: savedCards, topic: savedTopic, reading } = JSON.parse(saved);
      const lastDate = new Date(date);
      if ((new Date() - lastDate) / (1000 * 60 * 60 * 24) < 3) {
        setStoredDate(lastDate);
        setTopic(savedTopic);
        setCards(savedCards.map(s => ({
          ...oracleDeck.find(c => c.id === s.id),
          data: oracleDeck.find(c => c.id === s.id)[lang],
          isReversed: s.isReversed,
          isRevealed: true
        })));
        setRevealedCount(3);
        setAiReading(reading?.text || '');
      }
    }
  }, [lang]);

  const handleStartSpread = () => {
    if (isDrawing || !topic.trim()) return;
    setIsDrawing(true);
    setTimeout(() => {
      const selected = [...oracleDeck].sort(() => 0.5 - Math.random()).slice(0, 3).map(card => ({
        data: card[lang],
        image: card.image,
        originalId: card.id,
        isReversed: card.allowInvertedAdvice && Math.random() < 0.5,
        isRevealed: false
      }));
      setCards(selected);
      setIsDrawing(false);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ date: new Date().toISOString(), topic, cards: selected.map(c => ({ id: c.originalId, isReversed: c.isReversed })) }));
    }, 1500);
  };

  const handleGetAIReading = async () => {
    if (isLoadingAi || aiReading) return;
    setIsLoadingAi(true);
    
    const cardsForAi = cards.map((c, index) => ({
        name: `${c.data.name} - ${c.data.category}`,
        positionName: index === 0 ? localUi.pos1 : index === 1 ? localUi.pos2 : localUi.pos3,
        isReversed: c.isReversed
    }));

    const reading = await getOracleReading(topic, localUi.spreadTitle, cardsForAi, lang);
    if (reading.success) {
      setAiReading(reading.text);
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...saved, reading }));
    }
    setIsLoadingAi(false);
  };

  const resetSpread = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCards([]);
    setTopic('');
    setRevealedCount(0);
    setAiReading('');
  };

  if (cards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 min-h-[70vh] w-full max-w-4xl mx-auto">
        <h1 style={titleStyle} className="text-4xl font-bold mb-4">{localUi.spreadTitle}</h1>
        <p className="text-gray-400 mb-8">{localUi.spreadIntro}</p>
        <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={localUi.inputPlaceholder} className="w-full max-w-sm bg-gray-900 text-white p-4 rounded-lg border border-gray-700 mb-8 text-center" />
        <button onClick={handleStartSpread} disabled={isDrawing || !topic} className="w-48 h-64 border-2 border-dashed border-gray-600 rounded-xl flex items-center justify-center hover:border-amber-500 transition-all">
          {isDrawing ? "Conectando..." : <LockClosedIcon className="w-12 h-12 text-gray-500"/>}
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-4 flex flex-col items-center">
      <h2 className="text-2xl font-bold text-amber-500 mb-12">"{topic}"</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 w-full place-items-center">
        {cards.map((card, idx) => (
          <CardSlot 
            key={idx} 
            card={card} 
            isReversed={card.isReversed} 
            isRevealed={card.isRevealed} 
            isCenter={idx === 1}
            onClick={() => {
              const newCards = [...cards];
              newCards[idx].isRevealed = true;
              setCards(newCards);
              setRevealedCount(prev => prev + 1);
            }}
            positionLabel={idx === 0 ? localUi.pos1 : idx === 1 ? localUi.pos2 : localUi.pos3}
          />
        ))}
      </div>

      {revealedCount === 3 && (
        <div className="w-full max-w-3xl flex flex-col items-center gap-8">
           {!aiReading ? (
             <button 
                disabled={isLoadingAi}
                onClick={handleGetAIReading} 
                className={`flex items-center gap-2 px-8 py-3 rounded-full transition-all ${isLoadingAi ? 'bg-gray-600 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-500 hover:scale-105'}`}
             >
                {isLoadingAi ? "Interpretando..." : <><SparklesIcon className="w-5 h-5"/> {localUi.aiBtn}</>}
             </button>
           ) : (
             <AIReadingDisplay interpretation={aiReading} isLoading={isLoadingAi} />
           )}

           <CooldownTimer startDate={storedDate} daysLimit={3} lang={lang} />

           <div className="mt-8 pt-8 border-t border-gray-800 w-full flex flex-col items-center">
              <OracleCreditManager lang={lang} onCreditUsed={resetSpread} />
           </div>
        </div>
      )}
    </div>
  );
};

export default TripleEnergySpread;